/**
 * PharmacyInventory — admin view of a single pharmacy's inventory.
 *
 * Route: /admin/pharmacy-inventory/:pharmacyId
 *
 * Two tabs:
 *   • Master Catalogue — browse/search MedsHub drugs & OTC, pull items into the
 *     pharmacy's inventory with a price.
 *   • Current Inventory — view/edit price & stock, toggle on-sale, remove.
 *
 * Talks to the same backend the pharmacy app uses (routes mounted at /pharmacy):
 *   GET    /pharmacy/medshub/catalog
 *   POST   /pharmacy/medshub/pull
 *   GET    /pharmacy/medshub/inventory/:pharmacyId
 *   PATCH  /pharmacy/medshub/inventory/:id
 *   DELETE /pharmacy/medshub/inventory/:id
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Avatar,
  Chip,
  Button,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Skeleton,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  MdArrowBack,
  MdSearch,
  MdClear,
  MdAdd,
  MdCheck,
  MdEdit,
  MdDelete,
  MdMedication,
  MdInventory2,
} from "react-icons/md";
import ImportInventoryTab from "./ImportInventoryTab";

const BASE_URL = "https://api.medstown.com";
const TEAL = "#004D4D";

// ── tiny debounce hook ────────────────────────────────────────────────────────
function useDebounce(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const rupee = (n) => (n == null || n === "" ? "—" : `₹${n}`);

// A–Z index. "#" buckets names that don't start with a letter (digits/symbols).
const ALPHABET = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ", "#"];

// ─────────────────────────────────────────────────────────────────────────────
// MASTER CATALOGUE TAB
// ─────────────────────────────────────────────────────────────────────────────
function CatalogTab({ pharmacyId, onPulled }) {
  const [source, setSource] = useState("drug");
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 250);
  // Active A–Z letter ("" = none). Letter and search are mutually exclusive.
  const [letter, setLetter] = useState("");

  const [rows, setRows] = useState([]);
  // totalCount is only returned for a bounded query (search/letter); for a plain
  // browse the backend omits it (too expensive over 450k rows), so we page off
  // `hasMore` instead. null = unknown total.
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0); // 0-based for MUI
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [pulledKeys, setPulledKeys] = useState(new Set());

  // pull dialog
  const [pullItem, setPullItem] = useState(null);
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [saving, setSaving] = useState(false);

  const reqId = useRef(0);

  const keyOf = (it) => `${source}:${it.id}`;

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        source,
        page: String(page + 1),
        limit: String(limit),
      });
      if (debounced) params.set("search", debounced);
      if (letter) params.set("letter", letter);
      if (pharmacyId) params.set("pharmacyId", pharmacyId);
      const res = await axios.get(`${BASE_URL}/pharmacy/medshub/catalog?${params}`);
      if (id !== reqId.current) return;
      const data = res.data?.data || {};
      setRows(data.list || []);
      setTotal(data.totalCount ?? null);
      setHasMore(!!data.hasMore);
      setPulledKeys((prev) => {
        const next = new Set(prev);
        for (const it of data.list || []) if (it.alreadyPulled) next.add(`${source}:${it.id}`);
        return next;
      });
    } catch (e) {
      if (id === reqId.current) toast.error("Couldn't load catalogue");
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [source, debounced, letter, page, limit, pharmacyId]);

  useEffect(() => {
    load();
  }, [load]);

  // reset to page 0 when the query / letter / tab changes
  useEffect(() => {
    setPage(0);
  }, [debounced, letter, source]);

  // Typing a query and picking a letter are mutually exclusive filters.
  const onSearchChange = (v) => {
    setSearch(v);
    if (v && letter) setLetter("");
  };
  const onPickLetter = (l) => {
    setSearch("");
    setLetter((prev) => (prev === l ? "" : l)); // click again to clear
  };

  const openPull = (it) => {
    setPullItem(it);
    const suggested = it.suggestedPrice ?? it.mrp ?? "";
    setPrice(suggested === "" ? "" : String(suggested));
    setStock("");
  };

  const submitPull = async () => {
    const p = Number(price);
    if (!(p > 0)) {
      toast.error("Enter a selling price greater than 0");
      return;
    }
    setSaving(true);
    try {
      const item = {
        source,
        id: pullItem.id,
        sellingPrice: p,
        ...(stock !== "" ? { stockQty: Number(stock) } : {}),
      };
      const res = await axios.post(`${BASE_URL}/pharmacy/medshub/pull`, {
        pharmacyId,
        items: [item],
      });
      const added = res.data?.data?.added ?? 0;
      setPulledKeys((prev) => new Set(prev).add(keyOf(pullItem)));
      toast.success(added ? `Added "${pullItem.name}" to inventory` : "Already in inventory");
      setPullItem(null);
      onPulled?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Couldn't add item");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {/* Controls */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MdSearch color="#64748b" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <MdClear />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={source}
          onChange={(_, v) => v && setSource(v)}
          sx={{ "& .Mui-selected": { backgroundColor: `${TEAL} !important`, color: "#fff !important" } }}
        >
          <ToggleButton value="drug">Prescription</ToggleButton>
          <ToggleButton value="otc">OTC</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* A–Z index — hidden while actively searching by text */}
      {!debounced && (
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          spacing={0.5}
          sx={{ mb: 1.5, rowGap: 0.5 }}
        >
          {ALPHABET.map((l) => {
            const active = letter === l;
            return (
              <Button
                key={l}
                onClick={() => onPickLetter(l)}
                size="small"
                variant={active ? "contained" : "text"}
                sx={{
                  minWidth: 30,
                  px: 0.5,
                  py: 0.25,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: active ? "#fff" : "#64748b",
                  backgroundColor: active ? TEAL : "transparent",
                  "&:hover": { backgroundColor: active ? "#003838" : "#f1f5f9" },
                }}
              >
                {l}
              </Button>
            );
          })}
        </Stack>
      )}

      {/* Result summary when filtering */}
      {(letter || debounced) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {total != null ? `${total.toLocaleString("en-IN")} ` : ""}
          {letter ? `in “${letter === "#" ? "0–9" : letter}”` : `matching “${debounced}”`}
        </Typography>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8fafc" }}>
              <TableCell>Medicine</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>MRP</TableCell>
              <TableCell align="center">Rx</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}>
                    <Skeleton height={36} />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  {debounced || letter ? "No matches found" : "No items in this category"}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((it) => {
                const pulled = pulledKeys.has(`${source}:${it.id}`);
                return (
                  <TableRow key={it.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar src={it.image} variant="rounded" sx={{ bgcolor: "#f1f5f9" }}>
                          <MdMedication color="#94a3b8" />
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 280 }}>
                          {it.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {it.company || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>{rupee(it.mrp)}</TableCell>
                    <TableCell align="center">
                      {it.rxRequired ? (
                        <Chip label="Rx" size="small" color="error" variant="outlined" />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {pulled ? (
                        <Chip icon={<MdCheck />} label="Added" size="small" color="success" variant="outlined" />
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<MdAdd />}
                          onClick={() => openPull(it)}
                          sx={{ backgroundColor: TEAL, "&:hover": { backgroundColor: "#003838" } }}
                        >
                          Add
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          // Plain browse has no known total → count -1 ("of more than X") and
          // gate the next button on hasMore. Search/letter give an exact count.
          count={total != null ? total : -1}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => {
            setLimit(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          nextIconButtonProps={total == null ? { disabled: !hasMore } : undefined}
          labelDisplayedRows={
            total != null
              ? undefined
              : ({ from, to }) => `${from}–${to}`
          }
        />
      </TableContainer>

      {/* Pull dialog */}
      <Dialog open={!!pullItem} onClose={() => setPullItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add to inventory</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {pullItem?.name}
            {pullItem?.mrp != null ? ` · MRP ₹${pullItem.mrp}` : ""}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Selling price (₹)"
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
            inputMode="decimal"
          />
          <TextField
            fullWidth
            margin="dense"
            label="Stock quantity (optional)"
            value={stock}
            onChange={(e) => setStock(e.target.value.replace(/[^0-9]/g, ""))}
            inputMode="numeric"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPullItem(null)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitPull}
            disabled={saving}
            sx={{ backgroundColor: TEAL, "&:hover": { backgroundColor: "#003838" } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CURRENT INVENTORY TAB
// ─────────────────────────────────────────────────────────────────────────────
function InventoryTab({ pharmacyId, refreshKey }) {
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 350);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);

  const [editItem, setEditItem] = useState(null);
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [saving, setSaving] = useState(false);

  const reqId = useRef(0);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page + 1), limit: String(limit) });
      if (debounced) params.set("search", debounced);
      const res = await axios.get(
        `${BASE_URL}/pharmacy/medshub/inventory/${pharmacyId}?${params}`
      );
      if (id !== reqId.current) return;
      const data = res.data?.data || {};
      setRows(data.list || []);
      setTotal(data.totalCount || 0);
    } catch (e) {
      if (id === reqId.current) toast.error("Couldn't load inventory");
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [pharmacyId, debounced, page, limit]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    setPage(0);
  }, [debounced]);

  const toggleSelling = async (row) => {
    const next = !row.isSelling;
    setRows((prev) => prev.map((r) => (r._id === row._id ? { ...r, isSelling: next } : r)));
    try {
      await axios.patch(`${BASE_URL}/pharmacy/medshub/inventory/${row._id}`, { isSelling: next });
    } catch (e) {
      setRows((prev) => prev.map((r) => (r._id === row._id ? { ...r, isSelling: row.isSelling } : r)));
      toast.error("Couldn't update");
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Remove "${row.name}" from inventory?`)) return;
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r._id !== row._id));
    setTotal((t) => Math.max(0, t - 1));
    try {
      await axios.delete(`${BASE_URL}/pharmacy/medshub/inventory/${row._id}`);
      toast.success("Removed");
    } catch (e) {
      setRows(snapshot);
      toast.error("Couldn't remove");
    }
  };

  const openEdit = (row) => {
    setEditItem(row);
    setPrice(row.sellingPrice != null ? String(row.sellingPrice) : "");
    setStock(row.stockQty != null ? String(row.stockQty) : "0");
  };

  const saveEdit = async () => {
    const p = Number(price);
    const s = Number(stock);
    if (!(p > 0)) return toast.error("Price must be greater than 0");
    if (!(s >= 0)) return toast.error("Stock can't be negative");
    setSaving(true);
    try {
      await axios.patch(`${BASE_URL}/pharmacy/medshub/inventory/${editItem._id}`, {
        sellingPrice: p,
        stockQty: s,
      });
      setRows((prev) =>
        prev.map((r) => (r._id === editItem._id ? { ...r, sellingPrice: p, stockQty: s } : r))
      );
      setEditItem(null);
      toast.success("Updated");
    } catch (e) {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Search your inventory…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MdSearch color="#64748b" />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch("")}>
                <MdClear />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8fafc" }}>
              <TableCell>Medicine</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell align="center">On sale</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}>
                    <Skeleton height={36} />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  {debounced ? "No matches in inventory" : "This pharmacy has no inventory yet"}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r._id} hover sx={{ opacity: r.isSelling ? 1 : 0.55 }}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar src={r.image} variant="rounded" sx={{ bgcolor: "#f1f5f9" }}>
                        <MdMedication color="#94a3b8" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 260 }}>
                          {r.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.company || ""}
                          {r.rxRequired ? " · Rx" : ""}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} sx={{ color: TEAL }}>
                      {rupee(r.sellingPrice)}
                    </Typography>
                    {r.mrp != null && r.mrp !== r.sellingPrice && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textDecoration: "line-through" }}
                      >
                        ₹{r.mrp}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Bulk-imported rows often carry an availability WORD
                        ("AVAILABLE") with no exact count — show that instead of
                        a misleading "0". */}
                    {r.stockQty > 0 ? (
                      r.stockQty
                    ) : r.stockStatus && r.stockStatus !== "OUT_OF_STOCK" ? (
                      <Chip
                        label={r.stockStatus === "UNKNOWN" ? "In stock" : r.stockStatus}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 22, fontSize: 11 }}
                      />
                    ) : r.stockStatus === "OUT_OF_STOCK" ? (
                      <Chip label="Out of stock" size="small" color="default" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
                    ) : (
                      r.stockQty ?? 0
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={!!r.isSelling}
                      onChange={() => toggleSelling(r)}
                      sx={{ "& .Mui-checked": { color: TEAL } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit price / stock">
                      <IconButton size="small" onClick={() => openEdit(r)} sx={{ color: TEAL }}>
                        <MdEdit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => removeRow(r)} color="error">
                        <MdDelete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => {
            setLimit(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>

      {/* Edit dialog */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit item</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {editItem?.name}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Selling price (₹)"
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
            inputMode="decimal"
          />
          <TextField
            fullWidth
            margin="dense"
            label="Stock quantity"
            value={stock}
            onChange={(e) => setStock(e.target.value.replace(/[^0-9]/g, ""))}
            inputMode="numeric"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditItem(null)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveEdit}
            disabled={saving}
            sx={{ backgroundColor: TEAL, "&:hover": { backgroundColor: "#003838" } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function PharmacyInventory() {
  const { pharmacyId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  // bumping this forces the inventory tab to refetch after a pull
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <ToastContainer position="bottom-right" autoClose={2500} />

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${TEAL} 0%, #006666 100%)`,
          color: "#fff",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: "#fff" }}>
            <MdArrowBack />
          </IconButton>
          <MdInventory2 size={26} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={800}>
              Pharmacy Inventory
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, fontFamily: "monospace" }}>
              {pharmacyId}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 1.5, md: 2 } }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 2,
            "& .MuiTab-root": { fontWeight: 700, textTransform: "none" },
            "& .Mui-selected": { color: `${TEAL} !important` },
            "& .MuiTabs-indicator": { backgroundColor: TEAL },
          }}
        >
          <Tab label="Master Catalogue" />
          <Tab label="Current Inventory" />
          <Tab label="Bulk Import" />
        </Tabs>

        {tab === 0 ? (
          <CatalogTab pharmacyId={pharmacyId} onPulled={() => setRefreshKey((k) => k + 1)} />
        ) : tab === 1 ? (
          <InventoryTab pharmacyId={pharmacyId} refreshKey={refreshKey} />
        ) : (
          <ImportInventoryTab
            pharmacyId={pharmacyId}
            onImported={() => setRefreshKey((k) => k + 1)}
          />
        )}
      </Paper>
    </Box>
  );
}
